import { Box, Flex, Text } from '@gazin/ecommerce-ui'
import DiscountTag from 'src/components/discountTag'
import PromotionTag from 'src/components/promotionTag'
import { usePaymentMethod } from 'src/contexto/hooks/usePaymentMethod'
import { Ad } from 'src/services/marketplace-api/dtos'
import { theme } from 'src/styles/themes/default'
import { formatarPreco } from 'src/util/formatar'
import { env } from 'src/configs/env'
import {
  arePixOrBilletAvailable,
  getInCashAvailablePaymentMethods,
  isCreditCardAvailable,
} from 'src/util/payment'
import { PaymentMethodsInformationModal } from 'src/components/paymentMethodsInformationModal'

type ProductValuesProps = {
  adActive: Ad
  promotionImages: {
    desktop: string
    mobile: string
  }
  discountPercentage?: number
}

export const ProductValues: React.FC<ProductValuesProps> = ({
  adActive,
  discountPercentage,
}) => {
  const { payment } = usePaymentMethod()
  const hasCreditCard = isCreditCardAvailable(payment)
  const hasPixOrBillet = arePixOrBilletAvailable(payment)
  const billet = adActive?.formas_pagamento.boleto
  const installment = adActive?.preco.parcelamento
  const installments = adActive?.formas_pagamento.cartao_credito?.parcelamentos

  const paymentMethodsBilletOrPix = getInCashAvailablePaymentMethods(payment)
  const price = adActive?.preco
  const isPreviousPrice = price.de > price.por
  const minimoPercentualDesconto = env.minimoPercentualDesconto

  const installmentsMaxQuantityWithoutInterest =
    installments?.filter((installment) => installment.taxa_juros === 0)
      .length || installment.quantidade

  return (
    <Box>
      {isPreviousPrice && (hasCreditCard || hasPixOrBillet) && (
        <Flex flexDir="row" mb="0.125rem">
          <Text
            color={theme.colors.grey[300]}
            fontSize="0.875rem"
            lineHeight="1.125rem"
            mb="0"
            textDecor="line-through"
          >
            {formatarPreco(price.de)}
          </Text>
          {discountPercentage !== undefined &&
            discountPercentage >= minimoPercentualDesconto && (
              <Box
                h="1.125rem"
                ml="0.5rem"
                w="2.688rem"
                data-testid="discount-tag"
              >
                <DiscountTag discountPercentage={discountPercentage} />
              </Box>
            )}
        </Flex>
      )}

      {hasPixOrBillet && (
        <Box color={theme.colors.grey[300]}>
          <Flex
            alignItems="baseline"
            mb="0.063rem"
            mt="0.125rem"
            maxW={{ base: '14.375rem', md: '18.4375rem' }}
            flexWrap="wrap"
            columnGap="0.5rem"
            rowGap={0}
          >
            <Text
              color={theme.primary.main}
              fontSize={{ base: '1.5rem', md: '1.875rem' }}
              fontWeight="700"
              lineHeight={{ base: '1.8156rem', md: '2.2694rem' }}
            >
              {formatarPreco(billet.por)}
            </Text>
            <Text
              lineHeight={{ base: '1.0588rem', md: '1.21rem' }}
              fontSize={{ base: '0.875rem', md: '1rem' }}
              color="neutral.600"
            >
              {`No ${paymentMethodsBilletOrPix}`}
            </Text>
          </Flex>
          {billet.valor_economia && (
            <Box mt="0.3rem" data-testid="promotion-tag">
              <PromotionTag discountValue={billet.valor_economia} />
            </Box>
          )}
        </Box>
      )}

      {hasCreditCard && (
        <Flex
          color={theme.colors.grey[300]}
          alignItems="center"
          gap="0.27rem"
          mb={{ base: '1rem', md: '1.5rem' }}
          fontSize={{ base: '0.875rem', md: '1rem' }}
          lineHeight={{ base: '1.0588rem', md: '1.21rem' }}
        >
          <Text fontWeight="700" lineHeight="1.125rem" mb="0">
            {formatarPreco(price.por)}
          </Text>
          <Text fontWeight="400" lineHeight="1.125rem" mb="0">
            em até <strong>{installmentsMaxQuantityWithoutInterest}x</strong>{' '}
            sem juros
          </Text>
        </Flex>
      )}

      <PaymentMethodsInformationModal
        installments={installments?.map(
          ({ quantidade, taxa_juros, valor, total }) => ({
            amount: valor,
            interestRate: taxa_juros,
            quantity: quantidade,
            total,
          })
        )}
        installmentsMaxQuantityWithoutInterest={
          installmentsMaxQuantityWithoutInterest
        }
        totalInCash={billet.por || 0}
        totalInInstallments={price.por || 0}
      />
    </Box>
  )
}
